/**
 * Record Health Panel
 * Shows completeness, blockers, and next-step guidance for any entity.
 */

import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle, AlertTriangle, XCircle, Info, ArrowRight,
  ShieldAlert, ChevronRight, User, Clock,
} from "lucide-react";
import type { UnifiedBlocker } from "@/data/unifiedBlockers";
import type { NextStep } from "@/data/nextStepEngine";
import { getBlockerSummary } from "@/data/unifiedBlockers";

interface Props {
  title?: string;
  blockers: UnifiedBlocker[];
  nextSteps: NextStep[];
  completeness: { label: string; done: boolean }[];
  compact?: boolean;
  onCompletenessClick?: (label: string) => void;
}

const SEVERITY_ICON = {
  hard: XCircle,
  warning: AlertTriangle,
  info: Info,
};
const SEVERITY_COLOR = {
  hard: "text-destructive",
  warning: "text-warning",
  info: "text-info",
};
const SEVERITY_BG = {
  hard: "bg-destructive/10 border-destructive/20",
  warning: "bg-warning/10 border-warning/20",
  info: "bg-info/10 border-info/20",
};

const PRIORITY_COLOR = {
  high: "text-destructive",
  medium: "text-warning",
  low: "text-muted-foreground",
};

export function RecordHealthPanel({ title = "Record Health", blockers, nextSteps, completeness, compact, onCompletenessClick }: Props) {
  const summary = getBlockerSummary(blockers);
  const completedCount = completeness.filter(c => c.done).length;
  const completenessPercent = completeness.length > 0 ? Math.round((completedCount / completeness.length) * 100) : 100;
  const statusLabel = summary.status === "blocked" ? "Blocked" : summary.status === "at_risk" ? "At Risk" : "Clear";
  const statusColor = summary.status === "blocked" ? "text-destructive" : summary.status === "at_risk" ? "text-warning" : "text-success";
  const StatusIcon = summary.status === "blocked" ? XCircle : summary.status === "at_risk" ? AlertTriangle : CheckCircle;

  return (
    <Card className="shadow-none">
      <CardContent className={compact ? "p-3" : "p-4"}>
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
            <ShieldAlert className="h-3.5 w-3.5" /> {title}
          </h3>
          <div className="flex items-center gap-1.5">
            <StatusIcon className={`h-3.5 w-3.5 ${statusColor}`} />
            <span className={`text-xs font-semibold ${statusColor}`}>{statusLabel}</span>
          </div>
        </div>

        {/* Completeness bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">Completeness</span>
            <span className="text-[10px] font-semibold">{completedCount}/{completeness.length} ({completenessPercent}%)</span>
          </div>
          <Progress value={completenessPercent} className="h-1.5" />
          {!compact && completeness.some(c => !c.done) && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {completeness.filter(c => !c.done).map(c => (
                <span
                  key={c.label}
                  className={`text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded ${onCompletenessClick ? "cursor-pointer hover:bg-muted/80 hover:text-foreground transition-colors" : ""}`}
                  onClick={onCompletenessClick ? () => onCompletenessClick(c.label) : undefined}
                >
                  ✗ {c.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Blockers */}
        {blockers.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Active Blockers ({blockers.length})
            </p>
            <div className="space-y-1">
              {blockers.slice(0, compact ? 3 : 10).map(b => {
                const Icon = SEVERITY_ICON[b.severity];
                return (
                  <div key={b.id} className={`flex items-start gap-2 px-2 py-1.5 rounded-md border text-xs ${SEVERITY_BG[b.severity]}`}>
                    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${SEVERITY_COLOR[b.severity]}`} />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{b.label}</span>
                      {!compact && <p className="text-[10px] text-muted-foreground">{b.detail}</p>}
                    </div>
                    {b.actionLink && (
                      <Link to={b.actionLink} className="shrink-0">
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                      </Link>
                    )}
                  </div>
                );
              })}
              {compact && blockers.length > 3 && (
                <p className="text-[10px] text-muted-foreground">+{blockers.length - 3} more</p>
              )}
            </div>
          </div>
        )}

        {/* Next Steps */}
        {nextSteps.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
              Next Steps
            </p>
            <div className="space-y-1">
              {nextSteps.slice(0, compact ? 2 : 6).map((step, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/40 text-xs">
                  <ArrowRight className={`h-3 w-3 shrink-0 ${PRIORITY_COLOR[step.priority]}`} />
                  <span className="flex-1">{step.action}</span>
                  <Badge variant="outline" className="text-[9px] shrink-0 gap-1">
                    <User className="h-2.5 w-2.5" /> {step.owner}
                  </Badge>
                  {step.link && (
                    <Link to={step.link}><ChevronRight className="h-3 w-3 text-muted-foreground hover:text-foreground" /></Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {blockers.length === 0 && nextSteps.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">
            <CheckCircle className="h-4 w-4 mx-auto mb-1 text-success" />
            All clear — no blockers or pending actions.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
